import { Button as MuiButton } from "@mui/material";
import { XIcon, type IconProps } from "@phosphor-icons/react";
import clsx from "clsx";
import {
  type ElementType,
  type MouseEventHandler,
  type ReactNode,
} from "react";
import { useIntl } from "react-intl";

import { IconButton } from "@/components/IconButton/IconButton";
import { Skeleton } from "@/components/Skeleton/Skeleton";

type SearchResultButtonProps = {
  onClick?: MouseEventHandler<HTMLButtonElement>;
  icon: ElementType<IconProps>;
  showIconBackground?: boolean;
  primaryText: ReactNode;
  secondaryText?: string;
  code?: string;
  onRemove?: () => void;
};

export function SearchResultButton({
  icon: Icon,
  showIconBackground = true,
  onClick,
  primaryText,
  secondaryText,
  code,
  onRemove,
}: SearchResultButtonProps) {
  const intl = useIntl();

  return (
    <div className="relative flex w-full items-center">
      <MuiButton
        fullWidth
        onClick={onClick}
        variant="text"
        sx={{
          p: "calc(var(--spacing) * 2) calc(var(--spacing) * 3) calc(var(--spacing) * 2) calc(var(--spacing) * 1.5)",
          paddingInlineEnd: onRemove
            ? "calc(var(--spacing) * 11)"
            : "calc(var(--spacing) * 1.5)",
          borderRadius: 4,
          gap: "calc(var(--spacing) * 3)",
        }}
      >
        <div
          className={clsx("shrink-0 rounded-xl p-1.5", {
            "bg-surface-background p-2.5": showIconBackground,
            "-me-1.5": !showIconBackground,
          })}
        >
          <Icon color="var(--color-text-primary)" size={20} />
        </div>
        <div className="grow overflow-hidden text-start text-sm whitespace-nowrap">
          <div className="overflow-hidden font-medium overflow-ellipsis text-text-primary">
            {primaryText}
          </div>
          {secondaryText ? (
            <div className="overflow-hidden font-normal overflow-ellipsis text-text-tertiary">
              {secondaryText}
            </div>
          ) : null}
        </div>
        {code ? (
          <div className="h-fit rounded-sm bg-surface-accent-softer px-1.5 py-0.5 text-sm font-medium text-surface-accent-strong uppercase">
            {code}
          </div>
        ) : null}
      </MuiButton>

      {onRemove ? (
        <div className="absolute end-1.5">
          <IconButton
            onClick={onRemove}
            type="button"
            size="small"
            aria-label={intl.formatMessage({
              id: "remove",
              defaultMessage: "Remove",
            })}
          >
            <XIcon size={20} />
          </IconButton>
        </div>
      ) : null}
    </div>
  );
}

type LocationListItemSkeletonProps = {
  showIconBackground?: boolean;
  showSecondaryText?: boolean;
  showLocationCode?: boolean;
  showRemoveButton?: boolean;
};

export function SearchResultButtonSkeleton({
  showIconBackground = false,
  showSecondaryText = false,
  showLocationCode = false,
  showRemoveButton = false,
}: LocationListItemSkeletonProps) {
  return (
    <div className="flex w-full items-center gap-3 rounded-2xl py-2">
      {showIconBackground ? (
        <Skeleton variant="rounded" width={40} height={40} />
      ) : (
        <div className="-me-1">
          <Skeleton variant="rounded" width={32} height={32} />
        </div>
      )}

      <div className="grow overflow-hidden text-start text-sm whitespace-nowrap">
        <div className="overflow-hidden font-medium overflow-ellipsis text-text-primary">
          <Skeleton variant="text" width="60%" />
        </div>

        {showSecondaryText ? (
          <div className="overflow-hidden font-normal overflow-ellipsis text-text-tertiary">
            <Skeleton variant="text" width="35%" />
          </div>
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        {showLocationCode ? (
          <Skeleton variant="rounded" width={40} height={24} />
        ) : null}

        {showRemoveButton ? (
          <Skeleton variant="rounded" width={30} height={30} />
        ) : null}
      </div>
    </div>
  );
}

type Props<T> = {
  heading?: ReactNode;
  data: T[];
  renderItem: (item: T) => ReactNode;
  getItemKey: (item: T) => React.Key;
};

export function SearchResultList<T>({
  heading,
  data,
  renderItem,
  getItemKey,
}: Props<T>) {
  return (
    <>
      {heading ? (
        <div className="w-full pt-4">
          <span className="text-base font-medium text-text-primary">
            {heading}
          </span>
        </div>
      ) : null}

      <ul
        style={{
          width: "calc(100% + 10px)",
        }}
        className="mt-2 ltr:-translate-x-[5px] rtl:translate-x-[5px]"
      >
        {data.map((item) => (
          <li key={getItemKey(item)}>{renderItem(item)}</li>
        ))}
      </ul>
    </>
  );
}
